import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../api';
import { Modal } from './Modal';
import type { Issue, Queue } from '../types';

/**
 * Создание задачи. Очередь выбирается здесь же — задача не существует вне очереди, и это
 * единственное поле, без которого её нельзя завести.
 */
export function CreateIssueModal({
  tenantId,
  queues,
  fixedQueueId,
  onClose,
  onCreated,
}: {
  tenantId: string;
  queues: Queue[];
  /** Со страницы очереди выбирать нечего — она уже выбрана. */
  fixedQueueId?: string;
  onClose: () => void;
  onCreated?: (issue: Issue) => void;
}) {
  const queryClient = useQueryClient();
  const [queueId, setQueueId] = useState(fixedQueueId ?? queues[0]?.id ?? '');
  const [summary, setSummary] = useState('');
  const [description, setDescription] = useState('');

  const create = useMutation({
    mutationFn: () =>
      api.post<Issue>(`/tenants/${tenantId}/queues/${queueId}/issues`, {
        summary,
        description: description || undefined,
      }),
    onSuccess: async (issue) => {
      await queryClient.invalidateQueries({ queryKey: ['issues', tenantId] });
      await queryClient.invalidateQueries({ queryKey: ['queues', tenantId] });
      onCreated?.(issue);
      onClose();
    },
  });

  return (
    <Modal title="Новая задача" onClose={onClose}>
      {!fixedQueueId && (
        <div className="form-group">
          <label className="form-label" htmlFor="issue-queue">
            Очередь
          </label>
          <select
            id="issue-queue"
            className="input"
            value={queueId}
            onChange={(event) => setQueueId(event.target.value)}
          >
            {queues.map((queue) => (
              <option key={queue.id} value={queue.id}>
                {queue.key} — {queue.name}
              </option>
            ))}
          </select>
        </div>
      )}

      <div className="form-group">
        <label className="form-label" htmlFor="issue-summary">
          Заголовок
        </label>
        <input
          id="issue-summary"
          className="input"
          value={summary}
          onChange={(event) => setSummary(event.target.value)}
          autoFocus
        />
      </div>

      <div className="form-group">
        <label className="form-label" htmlFor="issue-description">
          Описание
        </label>
        <textarea
          id="issue-description"
          className="input"
          value={description}
          onChange={(event) => setDescription(event.target.value)}
        />
      </div>

      {create.isError && <div className="form-error">{(create.error as ApiError).message}</div>}

      <div className="modal-actions">
        <button className="btn btn-secondary" onClick={onClose}>
          Отмена
        </button>
        <button
          className="btn btn-primary"
          disabled={!summary.trim() || !queueId || create.isPending}
          onClick={() => create.mutate()}
        >
          Создать
        </button>
      </div>
    </Modal>
  );
}